/**
 * Campaign Progress Module
 * Migrated from static/js/campaign-progress.js to ES module
 *
 * This module handles real-time campaign progress tracking,
 * auto-refresh based on campaign phase, and UI updates.
 */

// Configuration for refresh intervals by phase/status
const CONFIG = {
  refreshIntervals: {
    sampling: 2000,
    generating: 3000,
    generating_all: 3000,
    pending_approval: 5000,
    review_pending: 5000,
    sending: 10000,
    scheduled: 10000,
    processing: 3000,
    default: 3000
  }
};

// Internal state
let state = {
  refreshInterval: null,
  lastUpdateTime: Date.now(),
  campaignId: null,
  currentPhase: null,
  currentStatus: null,
  csrfToken: null,
  updateUrl: null
};

/**
 * Get CSRF token from the page
 * @returns {string}
 */
function getCsrfToken() {
  const token = document.querySelector('[name=csrfmiddlewaretoken]');
  return token ? token.value : '';
}

/**
 * Setup event listeners for visibility and unload
 */
function setupEventListeners() {
  // Stop refresh when page is hidden
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      console.log('[CampaignProgress] Page hidden, stopping refresh');
      stopAutoRefresh();
    } else {
      console.log('[CampaignProgress] Page visible, restarting refresh');
      startAutoRefresh();
    }
  });

  // Cleanup on page unload
  window.addEventListener('beforeunload', () => {
    stopAutoRefresh();
  });
}

/**
 * Start auto-refresh based on current phase
 */
function startAutoRefresh() {
  stopAutoRefresh(); // Clear any existing interval

  const activePhases = ['sampling', 'generating', 'generating_all', 'sending', 'pending_approval', 'processing'];
  const activeStatuses = ['sampling', 'generating_all', 'processing', 'scheduled', 'pending_approval'];

  if (!activePhases.includes(state.currentPhase) && !activeStatuses.includes(state.currentStatus)) {
    console.log('[CampaignProgress] No auto-refresh needed for phase:', state.currentPhase, 'status:', state.currentStatus);
    return;
  }

  const interval = CONFIG.refreshIntervals[state.currentPhase] ||
                  CONFIG.refreshIntervals[state.currentStatus] ||
                  CONFIG.refreshIntervals.default;

  console.log('[CampaignProgress] Starting auto-refresh with interval:', interval, 'ms');
  state.refreshInterval = setInterval(updateStatus, interval);
}

/**
 * Stop auto-refresh
 */
function stopAutoRefresh() {
  if (state.refreshInterval) {
    clearInterval(state.refreshInterval);
    state.refreshInterval = null;
  }
}

/**
 * Update status via AJAX
 */
async function updateStatus() {
  try {
    const response = await fetch(state.updateUrl, {
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    });

    if (!response.ok) {
      console.error('[CampaignProgress] Failed to fetch status:', response.status);
      return;
    }

    const data = await response.json();
    console.log('[CampaignProgress] Status update received:', data);

    // Handle validation failures
    if (data.all_invalid || data.phase === 'failed_validation') {
      handleValidationFailure(data);
      stopAutoRefresh();
      return;
    }

    // Check if phase or status changed - reload page for major transitions
    if (data.phase !== state.currentPhase || data.status !== state.currentStatus) {
      console.log('[CampaignProgress] Phase or status changed, reloading page');
      location.reload();
      return;
    }

    // Update progress based on phase
    if (data.phase === 'sampling' || data.phase === 'generating' || data.phase === 'generating_all' || data.phase === 'pending_approval') {
      updateGenerationProgress(data);
    } else if (data.phase === 'sending') {
      updateSendingProgress(data);
    }

    // Update validation warnings if present
    if (data.has_validation_issues) {
      showValidationWarning(data);
    }

    // Check if complete
    if (data.complete === true || data.is_complete === true) {
      console.log('[CampaignProgress] Campaign phase complete, reloading page');
      setTimeout(() => location.reload(), 1000);
    }

    state.lastUpdateTime = Date.now();

  } catch (error) {
    console.error('[CampaignProgress] Failed to update status:', error);
  }
}

/**
 * Update generation progress UI
 * @param {Object} data - Progress data from server
 */
function updateGenerationProgress(data) {
  // Update main progress bar
  updateProgressBar('main-progress-bar', data.percentage);
  updateProgressBar('sample-progress-bar', data.percentage);
  updateProgressBar('progress-bar', data.percentage);

  // Update progress text
  updateElementText('progress-text', `${Math.round(data.percentage || 0)}%`);
  updateElementText('sample-progress-text', `${Math.round(data.percentage || 0)}%`);

  // Update stats
  updateElementText('emails-generated', data.current_count);
  updateElementText('emails-expected', data.total_expected);
  updateElementText('total-contacts', data.sample_size || data.eligible_contacts || data.total_contacts);

  // Update progress count message
  if (data.current_count !== undefined && data.total_expected !== undefined) {
    updateElementText('sample-progress-count',
      `${data.current_count} of ${data.total_expected} emails generated`);
  }

  // Update time remaining from server-calculated estimate
  console.log('[CampaignProgress] estimated_seconds_remaining from server:', data.estimated_seconds_remaining);
  updateTimeRemaining(data.percentage, data.estimated_seconds_remaining);

  // Handle phase completion
  if (data.phase === 'pending_approval' && data.is_complete) {
    handleSampleComplete();
  }
}

/**
 * Update sending progress UI
 * @param {Object} data - Progress data from server
 */
function updateSendingProgress(data) {
  updateProgressBar('scheduling-progress', data.scheduling_percentage);
  updateProgressBar('sending-progress', data.sending_percentage || data.percentage);

  updateElementText('emails-sent', data.sent_count);
  updateElementText('emails-delivered', data.delivered_count);
  updateElementText('emails-failed', data.failed_count);
}

/**
 * Handle validation failure
 * @param {Object} data - Error data from server
 */
function handleValidationFailure(data) {
  const statusCard = document.querySelector('.status-card');
  if (statusCard) {
    statusCard.classList.remove('info', 'success');
    statusCard.classList.add('danger');
  }

  updateElementText('status-title', 'Validation Failed');
  updateElementText('status-description', data.message || 'All emails failed validation');

  const icon = document.querySelector('.status-icon i');
  if (icon) {
    icon.className = 'fas fa-exclamation-triangle';
  }

  const statusIcon = document.querySelector('.status-icon');
  if (statusIcon) {
    statusIcon.classList.remove('icon-float', 'icon-spin');
    statusIcon.classList.remove('primary');
    statusIcon.classList.add('danger');
  }

  // Hide progress section
  const progressSection = document.querySelector('.progress-section');
  if (progressSection) {
    progressSection.style.display = 'none';
  }

  // Add error message
  const cardBody = document.querySelector('.status-card .card-body');
  if (cardBody && !document.querySelector('.validation-error-alert')) {
    const errorAlert = document.createElement('div');
    errorAlert.className = 'alert alert-danger validation-error-alert mt-4';
    errorAlert.innerHTML = `
      <strong><i class="fas fa-exclamation-circle me-2"></i>Validation Error:</strong><br>
      ${data.message || 'All sample emails failed validation. Please check your contact list for valid email addresses.'}
    `;
    cardBody.appendChild(errorAlert);
  }
}

/**
 * Show validation warning
 * @param {Object} data - Warning data from server
 */
function showValidationWarning(data) {
  const existingWarning = document.getElementById('validation-warning');
  if (!existingWarning && data.skipped_count > 0) {
    const progressSection = document.querySelector('.progress-section');
    if (progressSection) {
      const warningDiv = document.createElement('div');
      warningDiv.id = 'validation-warning';
      warningDiv.className = 'alert validation-warning mt-3';
      warningDiv.innerHTML = `
        <div class="d-flex align-items-start">
          <i class="fas fa-exclamation-triangle me-3 mt-1" style="font-size: 1.5em; color: #f59e0b;"></i>
          <div>
            <strong>Email Validation Notice</strong>
            <p class="mb-0 mt-2">
              ${data.validation_message || `${data.skipped_count} email(s) failed validation and were removed.`}
            </p>
          </div>
        </div>
      `;
      progressSection.parentNode.insertBefore(warningDiv, progressSection.nextSibling);
    }
  }
}

/**
 * Update a progress bar element
 * @param {string} elementId - ID of the progress bar element
 * @param {number} percentage - Progress percentage (0-100)
 */
function updateProgressBar(elementId, percentage) {
  const progressBar = document.getElementById(elementId);
  if (progressBar && percentage !== undefined) {
    const pct = Math.min(Math.round(percentage), 100);
    progressBar.style.width = `${pct}%`;
    progressBar.setAttribute('aria-valuenow', pct);

    // Update text if exists
    const textElement = progressBar.querySelector('span') || progressBar;
    textElement.textContent = `${pct}%`;
  }
}

/**
 * Update element text content
 * @param {string} elementId - ID of the element
 * @param {*} value - Value to set
 */
function updateElementText(elementId, value) {
  const element = document.getElementById(elementId);
  if (element && value !== undefined && value !== null) {
    element.textContent = typeof value === 'number' ? value.toLocaleString() : value;
  }
}

/**
 * Format seconds into a human-readable time string
 * @param {number} seconds - Time in seconds
 * @returns {string} Formatted time string (e.g., "~2 min", "~1 hour")
 */
function formatTimeRemaining(seconds) {
  if (seconds < 60) {
    return '< 1 min';
  }

  const minutes = Math.ceil(seconds / 60);
  if (minutes <= 60) {
    return `~${minutes} min`;
  }

  const hours = Math.ceil(minutes / 60);
  return hours === 1 ? '~1 hour' : `~${hours} hours`;
}

/**
 * Update time remaining display from server-provided estimate
 * @param {number} percentage - Current progress percentage
 * @param {number|null} estimatedSeconds - Server-calculated seconds remaining, or null
 */
function updateTimeRemaining(percentage, estimatedSeconds) {
  const timeElement = document.getElementById('time-remaining');
  if (!timeElement) {
    console.log('[CampaignProgress] time-remaining element not found');
    return;
  }

  // Show check mark when complete
  if (percentage >= 100) {
    timeElement.innerHTML = '<i class="fas fa-check"></i>';
    return;
  }

  // Don't show anything if no progress yet
  if (percentage <= 0) {
    return;
  }

  // Display server-provided estimate
  if (estimatedSeconds !== null && estimatedSeconds !== undefined && estimatedSeconds > 0) {
    const timeText = formatTimeRemaining(estimatedSeconds);
    console.log('[CampaignProgress] Time remaining from server:', estimatedSeconds, 's ->', timeText);
    timeElement.textContent = timeText;
  } else {
    // Show "Calculating..." while waiting for first estimate
    timeElement.textContent = 'Calculating...';
  }
}

/**
 * Handle sample completion
 */
function handleSampleComplete() {
  updateElementText('status-title', 'Sample Emails Ready for Review!');
  updateElementText('status-description',
    'Your sample emails are ready. Review them before generating the full campaign.');

  const icon = document.querySelector('.status-icon i');
  if (icon) {
    icon.className = 'fas fa-check-circle';
  }

  const statusIcon = document.querySelector('.status-icon');
  if (statusIcon) {
    statusIcon.classList.remove('icon-float', 'icon-spin');
    statusIcon.classList.add('celebration-icon');
  }
}

/**
 * Show progress UI for sample generation
 */
function showSampleProgressUI() {
  const generateSection = document.getElementById('generate-section');
  if (!generateSection) return;

  generateSection.innerHTML = `
    <div class="card status-card info">
      <div class="card-body text-center py-5">
        <div class="status-icon-container">
          <div class="status-icon primary icon-float">
            <i class="fas fa-cog icon-spin"></i>
          </div>
          <div class="status-ring primary"></div>
        </div>

        <h3 class="mb-3" id="status-title">
          <span class="text-gradient">Generating Sample Emails...</span>
        </h3>

        <p class="status-message" id="status-description">
          Validating email addresses and generating personalized content...
        </p>

        <div class="progress-section">
          <h5><i class="fas fa-tasks me-2"></i>Generation Progress</h5>
          <div class="progress mb-4">
            <div id="sample-progress-bar"
                 class="progress-bar progress-bar-striped progress-bar-animated"
                 role="progressbar"
                 style="width: 0%"
                 aria-valuenow="0"
                 aria-valuemin="0"
                 aria-valuemax="100">
              <span>0%</span>
            </div>
          </div>

          <div class="stat-grid">
            <div class="stat-box">
              <div class="stat-value" id="total-contacts">--</div>
              <div class="stat-label">Valid Contacts</div>
            </div>
            <div class="stat-box">
              <div class="stat-value text-primary" id="emails-generated">0</div>
              <div class="stat-label">Generated</div>
            </div>
            <div class="stat-box">
              <div class="stat-value" id="emails-expected">--</div>
              <div class="stat-label">Expected</div>
            </div>
            <div class="stat-box">
              <div class="stat-value text-success" id="time-remaining">
                <span class="skeleton-loader" style="display: inline-block; width: 80px; height: 30px;"></span>
              </div>
              <div class="stat-label">Time Left</div>
            </div>
          </div>
        </div>

        <p class="text-muted mt-3" id="sample-progress-count">
          <small>Validating email addresses and generating personalized content...</small>
        </p>
      </div>
    </div>
  `;
}

/**
 * Start sample generation
 * @param {string} url - Generation endpoint URL
 * @param {string} csrfToken - CSRF token
 * @returns {Promise<boolean>}
 */
async function startSampleGeneration(url, csrfToken) {
  console.log('[CampaignProgress] Starting sample generation');

  // Show progress UI
  showSampleProgressUI();

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-CSRFToken': csrfToken,
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: 'action=generate'
    });

    const data = await response.json();

    if (data.success) {
      // Update state and start tracking
      state.currentPhase = 'sampling';
      state.currentStatus = 'sampling';
      startAutoRefresh();
      return true;
    } else {
      console.error('[CampaignProgress] Failed to start generation:', data.error);
      alert('Failed to start generation: ' + (data.error || 'Unknown error'));
      location.reload();
      return false;
    }
  } catch (error) {
    console.error('[CampaignProgress] Error starting generation:', error);
    alert('Failed to start sample generation. Please try again.');
    location.reload();
    return false;
  }
}

/**
 * Initialize the campaign progress module
 * @param {Object} options - Configuration options
 * @param {number} options.campaignId - Campaign ID
 * @param {string} options.currentPhase - Current campaign phase
 * @param {string} options.currentStatus - Current campaign status
 * @param {string} [options.csrfToken] - CSRF token
 * @param {string} [options.updateUrl] - URL for status updates
 */
export function init(options) {
  state.campaignId = options.campaignId;
  state.currentPhase = options.currentPhase;
  state.currentStatus = options.currentStatus;
  state.csrfToken = options.csrfToken || getCsrfToken();
  state.updateUrl = options.updateUrl || window.location.href;

  console.log('[CampaignProgress] Initialized with:', {
    campaignId: state.campaignId,
    phase: state.currentPhase,
    status: state.currentStatus
  });

  startAutoRefresh();
  setupEventListeners();
}

/**
 * Set internal state - exported for testing purposes only
 * @param {Object} newState - State properties to set
 */
function setStateForTesting(newState) {
  Object.assign(state, newState);
}

/**
 * Get internal state - exported for testing purposes only
 * @returns {Object} Current internal state
 */
function getStateForTesting() {
  return { ...state };
}

// Public API
export {
  startAutoRefresh,
  stopAutoRefresh,
  updateStatus,
  showSampleProgressUI,
  startSampleGeneration,
  // Exported for testing
  formatTimeRemaining,
  updateTimeRemaining,
  updateGenerationProgress,
  setStateForTesting,
  getStateForTesting
};

// Default export for convenience
export default {
  init,
  startAutoRefresh,
  stopAutoRefresh,
  updateStatus,
  showSampleProgressUI,
  startSampleGeneration
};
